import type { ApiClient } from '@classgo/app-kit';
import type {
  CopyTimetableInput,
  CreateTimetableBlockInput,
  TimetableBlock,
  TimetableCopyResult,
  TimetableView,
  UpdateTimetableBlockInput,
} from '@classgo/shared';
import { queryOptions } from '@tanstack/react-query';

const BLOCKS = '/school/timetable/blocks';

const week = (query: Record<string, string | undefined>) => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value) params.set(key, value);
  }
  return `/school/timetable?${params.toString()}`;
};

/**
 * The timetable's write side (#113 over the API of #104): blocks placed, moved and removed one by
 * one, and a whole week copied from another group. A 409 carries the colliding blocks in
 * `ApiError.details.conflicts`, which the screen shows next to the form.
 *
 * @param {ApiClient} api -- The client.
 * @returns The calls.
 */
export const timetableApi = (api: ApiClient) => ({
  create: (body: CreateTimetableBlockInput) =>
    api.request<TimetableBlock>(BLOCKS, { method: 'POST', body }),
  update: (id: string, body: UpdateTimetableBlockInput) =>
    api.request<TimetableBlock>(`${BLOCKS}/${id}`, { method: 'PATCH', body }),
  remove: (id: string) => api.request<unknown>(`${BLOCKS}/${id}`, { method: 'DELETE' }),
  copy: (body: CopyTimetableInput) =>
    api.request<TimetableCopyResult>('/school/timetable/copy', { method: 'POST', body }),
});

/**
 * The weekly views (`GET /school/timetable`): a group's week, a teacher's week and a classroom's
 * occupancy in one school year. Every key starts with `['school', 'timetable']`, so one
 * invalidation after a write refreshes the three of them.
 */
export const timetableQueries = {
  group: (api: ApiClient, groupId: string) =>
    queryOptions({
      queryKey: ['school', 'timetable', 'group', groupId],
      queryFn: () => api.request<TimetableView>(week({ groupId })),
      enabled: groupId !== '',
    }),
  teacher: (api: ApiClient, teacherId: string, schoolYearId: string) =>
    queryOptions({
      queryKey: ['school', 'timetable', 'teacher', teacherId, schoolYearId],
      queryFn: () => api.request<TimetableView>(week({ teacherId, schoolYearId })),
      enabled: teacherId !== '' && schoolYearId !== '',
    }),
  classroom: (api: ApiClient, classroomId: string, schoolYearId: string) =>
    queryOptions({
      queryKey: ['school', 'timetable', 'classroom', classroomId, schoolYearId],
      queryFn: () => api.request<TimetableView>(week({ classroomId, schoolYearId })),
      enabled: classroomId !== '' && schoolYearId !== '',
    }),
};
